// @ts-expect-error — js only library
import Faye from 'faye'

type Unsubscribe = () => void

interface FayeSubscription extends PromiseLike<void> {
  cancel: () => void
}

interface FayeClient {
  subscribe: (
    channel: string,
    cb: (message: unknown) => void
  ) => FayeSubscription
  disable: (feature: 'websocket') => void
  setHeader: (name: string, value: string) => void
}

type FayeConnectionOptions = {
  url: string
  authorization?: string
}

const createFayeClient = ({
  url,
  authorization,
}: FayeConnectionOptions): FayeClient => {
  const client = new Faye.Client(url) as FayeClient

  // Browsers cannot send custom headers on a websocket handshake, so header auth needs long-polling.
  if (authorization !== undefined) {
    client.disable('websocket')
    client.setHeader('Authorization', authorization)
  }

  return client
}

type FayeConnection = { client: FayeClient }

const createLazyConnection = (init: () => Promise<FayeConnection>) => {
  let connection: Promise<FayeConnection> | undefined

  return () => {
    connection ??= init().catch((error: unknown) => {
      connection = undefined
      throw error
    })

    return connection
  }
}

const parseMessage = (message: unknown): unknown =>
  typeof message === 'string' ? JSON.parse(message) : message

const subscribeToChannel = async (
  client: FayeClient,
  channel: string,
  callback: (message: unknown) => unknown
): Promise<Unsubscribe> => {
  const subscription = client.subscribe(channel, (message) => {
    callback(parseMessage(message))
  })

  await subscription

  return () => subscription.cancel()
}

export { createFayeClient, createLazyConnection, subscribeToChannel }
export type { FayeClient, Unsubscribe }
